/**
 * Loads the `<lucent-insight>` Web Component bundle from insights-api.
 *
 * The bundle URL is built from the build-time `VITE_INSIGHTS_API_URL`. It used
 * to come from `/api/embed/runtime-config`, an endpoint on the dashboard
 * backend that existed purely so the SPA could ask where insights-api lived —
 * a question with no answerer now that the backend is gone.
 *
 * **The same-origin fallback is gone too, deliberately.** It returned
 * `window.location.origin` when the config fetch failed, which was a
 * reasonable guess when the dashboard and API were colocated. Under a static
 * deploy they never are, so the fallback could only ever produce a confusing
 * 404 for `runtime.js` on the static host instead of a clear configuration
 * error. `env.ts` now fails loudly at import if the variable is missing.
 *
 * We append the bundle as a `<script type="module">` element. The runtime
 * registers `<lucent-insight>` as a side effect on load.
 */

import { INSIGHTS_API_URL } from "./env.ts";

let loadingPromise: Promise<void> | null = null;

/**
 * Inject the `<lucent-insight>` runtime once. Idempotent — repeated
 * calls return the same promise so concurrent component mounts don't
 * each fire their own load.
 */
export const bootstrapRuntime = (): Promise<void> => {
  if (loadingPromise) return loadingPromise;
  loadingPromise = injectScript(`${INSIGHTS_API_URL}/api/embed/runtime.js`);
  return loadingPromise;
};

/**
 * Append `<script type="module" src="…">` and resolve when it loads.
 *
 * If the script fails (network, 5xx), the promise rejects with the
 * URL in the message so consumers can surface a useful error.
 */
const injectScript = (url: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-lucent-runtime="${url}"]`,
    );
    if (existing) {
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.type = "module";
    tag.src = url;
    tag.dataset.lucentRuntime = url;
    tag.onload = () => resolve();
    tag.onerror = () => reject(new Error(`failed to load runtime: ${url}`));
    document.head.appendChild(tag);
  });
